import { Component, inject, Input, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProcessProgressView, ShimmerVariant } from '@optima-stride-ai/optima-components';
import { LoggerService } from '@optima-stride-ai/core';
import { Router } from '@angular/router';
import { LibraryService } from '../libraries/library.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GlobalStateService } from '../../../service/global.service';
import { DocumentLoaderComponent } from '../../shared/components/document-loader/document-loader.component';
import { EntityLayoutConfigService } from '../../services/entity-layout-config.service';
import { ProgressIndicatorComponent } from '@optima-stride-ai/optima-components';
import { Routes, SessionStorageKeys } from '../../shared/enums/app-constants';

@Component({
  selector: 'app-video-viewer',
  imports: [CommonModule, ProgressIndicatorComponent],
  templateUrl: './video-viewer.component.html',
  styleUrls: ['./video-viewer.component.sass'],
})
export class VideoViewerComponent implements OnInit, OnDestroy {
  @Input() id?: string;
  private router = inject(Router);
  readonly ProcessProgressView = ProcessProgressView;
  private libraryService = inject(LibraryService);
  private sanitizer = inject(DomSanitizer);
  private logger = inject(LoggerService);
  private entityLayoutConfigService = inject(EntityLayoutConfigService);

  // State signals
  videoName = signal<string>('');
  isLoading = signal<boolean>(true);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  videoUrl = signal<SafeResourceUrl | null>(null);

  // Expose enums for template
  readonly ShimmerVariant = ShimmerVariant;

  constructor(private global: GlobalStateService) { }

  ngOnInit(): void {
    this.global.setIsPreviewOpen(true);
    this.loadVideo();
  }

  ngOnDestroy(): void {
    // Clear entity layout cache to force fresh thumbnail fetch on dashboard
    if (this.id) {
      this.entityLayoutConfigService.clearEntityCache(this.id);
    }
    
    this.global.setIsPreviewOpen(false);
  }

  private async loadVideo(): Promise<void> {
    try {
      // Get video ID from sessionStorage (set by libraries-page)
      const videoBizEntityId = this.id;;

      if (!videoBizEntityId) {
        this.hasError.set(true);
        this.errorMessage.set('No video specified');
        this.isLoading.set(false);
        return;
      }

      this.videoName.set(`Video-${videoBizEntityId}`);

      // Get the authenticated video URL from the service
      const rawUrl = await this.libraryService.getDocumentUrl(videoBizEntityId);

      // Sanitize the URL for security
      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
      this.videoUrl.set(safeUrl);

    } catch (error) {
      this.logger.error(`❌ Error loading video: ${error}`);
      this.hasError.set(true);
      this.errorMessage.set(error instanceof Error ? error.message : 'Failed to load video');
    } finally {
      this.isLoading.set(false);
    }
  }

  goBack(): void {
    // Clear sessionStorage
    sessionStorage.removeItem(SessionStorageKeys.VIDEO_BIZ_ENTITY_ID);

    // Navigate back to libraries
    this.router.navigate([Routes.LIBRARIES]);
  }

  retryLoad(): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.errorMessage.set('');
    this.loadVideo();
  }
}